import { Base } from "ui/Scripts/source/framework/base";
import { IChartItem, ChartItem } from "../chart/chartItem";
import { IReportItem, ReportItem } from "./reportItem";
import { ISavedReport, SavedReport } from "./savedReport";
import { ApplicationPermissionType, ReportSharingType } from "ui/Scripts/source/models/enums";
import { AppUtils } from "source/framework/appUtils";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";

export interface IReportsState {
    reports: IReportItem[];
    savedReports: IReportItem[];
    activeReportId: string;
    activeSavedReportId: string;
    reportChartItems: IChartItem[];
}

export interface IReportItems {
    items: IReportItem[];
    userReports: ISavedReport[];
    companyReports: ISavedReport[];
    systemReports: ISavedReport[];
    chartItems: IChartItem[];
}

export class ReportItems implements IReportItems {
    items: ReportItem[];
    userReports: SavedReport[];
    companyReports: SavedReport[];
    systemReports: SavedReport[];
    chartItems: ChartItem[];

    constructor();
    constructor(obj: IReportItems);
    constructor(obj?: any) {
        this.items = Base.getTypedArray(obj ? obj.items : null, ReportItem);
        this.userReports = Base.getTypedArray(obj ? obj.userReports : null, SavedReport);
        this.companyReports = Base.getTypedArray(obj ? obj.companyReports : null, SavedReport);
        this.systemReports = Base.getTypedArray(obj ? obj.systemReports : null, SavedReport);
        this.chartItems = Base.getTypedArray(obj ? obj.chartItems : null, ChartItem);
    }

    static getReportsState = async (companyId: string, activeReportId: string, activeSavedReportId: string, staticReports: ReportItem[], serviceCall: (companyId: string) => Promise<ReportItems>, onBegin: () => void, onAfter: () => void): Promise<IReportsState> => {
        if (onBegin) {
            onBegin();
        }
        try {
            const reportItems = await AppUtils.callService(() => serviceCall(companyId), false, true);
            if (!reportItems) return null;
            const reports = staticReports.concat(reportItems.items);
            const savedReports: ReportItem[] = [];
            for (const userReport of reportItems.userReports) {
                const report = reports.find(i => i.id === userReport.reportId);
                if (!report) continue;
                savedReports.push(ReportItem.createReportItemFromSavedReport(report, userReport, ReportSharingType.NotShared));
            }
            for (const companyReport of reportItems.companyReports) {
                const report = reports.find(i => i.id === companyReport.reportId);
                if (!report) continue;
                savedReports.push(ReportItem.createReportItemFromSavedReport(report, companyReport, ReportSharingType.Company));
            }
            for (const systemReport of reportItems.systemReports) {
                const report = reports.find(i => i.id === systemReport.reportId);
                if (!report) continue;
                savedReports.push(ReportItem.createReportItemFromSavedReport(report, systemReport, ReportSharingType.AllCompanies));
            }
            ReportItem.sortReportItems(reports);
            ReportItem.sortReportItems(savedReports);
            const activeSavedReport = savedReports.find(i => i.id === activeSavedReportId);
            const activeReport = !activeSavedReport
                ? reports.find(i => i.id === activeReportId)
                : null;
            return {
                reports,
                savedReports,
                activeReportId: activeReport
                    ? activeReportId
                    : (!activeSavedReport
                        ? reportItems.items[0].id
                        : ""),
                activeSavedReportId: activeSavedReport
                    ? activeSavedReportId
                    : "",
                reportChartItems: reportItems.chartItems
            };
        } finally {
            if (onAfter) {
                onAfter();
            }
        }
    };
}

const setStateAfterActiveReportUpdate = <T extends IReportsState, K>(
    state: T,
    getFilterStatePropsFromGeneralFilter: (generalFilter: any) => K,
    getListFilterIsSet: (state: T) => boolean,
    report: IReportItem,
    reports: IReportItem[],
    savedReports: IReportItem[],
    activeReportId: string,
    activeSavedReportId: string,
    additionalGeneralFilter: any = null,
    additionalReportFilter: any = null): K & {
        reports: IReportItem[],
        savedReports: IReportItem[],
        activeReportId: string,
        activeSavedReportId: string,
        reportFilterReport: IReportItem,
        listFilterIsSet: boolean
    } => {
    report.resetTaskDataRowToOrg();
    if (additionalGeneralFilter) {
        report.generalFilter = { ...report.generalFilter, ...additionalGeneralFilter };
    }
    if (additionalReportFilter) {
        report.reportFilter = { ...report.reportFilter, ...additionalReportFilter };
        report.copyReportParameterValuesFromSavedValues();
    }
    const reportFilterProps = getFilterStatePropsFromGeneralFilter(report.generalFilter); //Reset filter parameters
    const updatedState = {
        ...reportFilterProps,
        reports,
        savedReports,
        activeReportId,
        activeSavedReportId,
        reportFilterReport: report,
    };
    const newState = {
        ...state,
        ...updatedState
    };
    return {
        ...updatedState,
        listFilterIsSet: getListFilterIsSet(newState)
    };
};

export const getStateChangeAfterChangeActiveReport = <T extends IReportsState, K>(
    state: T,
    getFilterStatePropsFromGeneralFilter: (generalFilter: any) => K,
    getListFilterIsSet: (state: T) => boolean,
    activeReportId: string,
    additionalGeneralFilter: any = null,
    additionalReportFilter: any = null): K & {
        reports: IReportItem[],
        activeReportId: string,
        activeSavedReportId: string,
        listFilterIsSet: boolean
    } => {
    const reports = state.reports.slice(0);
    const report = reports.find(i => i.id === activeReportId);
    if (!report) return;
    return setStateAfterActiveReportUpdate(state, getFilterStatePropsFromGeneralFilter, getListFilterIsSet, report, reports, state.savedReports, activeReportId, "", additionalGeneralFilter, additionalReportFilter);
};

export const getStateChangeAfterChangeActiveSavedReport = <T extends IReportsState, K>(
    state: T,
    getFilterStatePropsFromGeneralFilter: (generalFilter: any) => K,
    getListFilterIsSet: (state: T) => boolean,
    activeSavedReportId: string,
    additionalGeneralFilter: any = null,
    additionalReportFilter: any = null): K & {
        savedReports: IReportItem[],
        activeReportId: string,
        activeSavedReportId: string,
        listFilterIsSet: boolean,
        reportFilterReport: IReportItem
    } => {
    const savedReports = state.savedReports.slice(0);
    const savedReport = savedReports.find(i => i.id === activeSavedReportId);
    if (!savedReport) return;
    return setStateAfterActiveReportUpdate(state, getFilterStatePropsFromGeneralFilter, getListFilterIsSet, savedReport, state.reports, savedReports, "", activeSavedReportId, additionalGeneralFilter, additionalReportFilter);
};

export const handleRemoveSavedReport = async <T extends IReportsState & { activeCompanyId: string }, K>(
    state: T,
    getFilterStatePropsFromGeneralFilter: (generalFilter: any) => K,
    getListFilterIsSet: (state: T) => boolean,
    applicationPermissionType: ApplicationPermissionType,
    removeReportFromDb: (userReports: IReportItem[], companyId: string, reportId: string, reportSharingType: ReportSharingType, applicationPermissionType: number) => Promise<ApiSuccess>,
    reportId: string): Promise<K & {
        savedReports: IReportItem[],
        activeReportId: string,
        activeSavedReportId: string,
        listFilterIsSet: boolean,
        reportFilterReport: IReportItem
    }> => {
    const savedReport = state.savedReports.find(i => i.id === reportId);
    if (!savedReport) return;
    const result = await removeReportFromDb(state.savedReports, state.activeCompanyId, reportId, savedReport.reportSharingType, applicationPermissionType);
    if (!result) return;
    const savedReports = state.savedReports.filter(i => i.id !== reportId);
    let activeReportId = "";
    let activeSavedReportId = "";
    let newReport: IReportItem = null;
    if (savedReports.length > 0) {
        newReport = savedReports[0];
        activeSavedReportId = newReport.id;
    } else if (state.reports.length > 0) {
        newReport = state.reports[0];
        activeReportId = newReport.id;
    }
    const reportFilterProps = getFilterStatePropsFromGeneralFilter(newReport?.generalFilter);
    const newState = { ...state, ...reportFilterProps, activeReportId, activeSavedReportId, savedReports };
    return {
        ...reportFilterProps,
        activeReportId,
        activeSavedReportId,
        savedReports,
        reportFilterReport: newReport,
        listFilterIsSet: getListFilterIsSet(newState)
    };
};